import { useMemo, useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SelectOption } from '~/shared/utils/types';
import { refreshStateMenuItems } from '~/shared/components/refreshStateMenuItems';
import { splitAccountFilters, splitProcessedFilterParams } from '~/shared/utils/apiFilters';
import {
  ActionTypeFilterValue,
  actionTypeFilterToApiType,
} from '~/shared/components/filters/actionTypeFilter';

import { getApiFilterFromSelectOption } from '../../common/helpers';
import { CUSTOM_DATES_TITLE } from '../../common/dateFilterMenuItems';
import { getTableDateRange, parseSortField } from '../helpers';

const OPTIMIZATION_TABLE_SORT_KEY = 'sort_field';

interface UseOptimizationTableParamsArgs {
  activeDateFilter: string | null;
  apiFilters: string | null;
  dataLimit: number;
  pageOffset: string;
  activeRefreshState?: string | null;
  accountsScope?: string;
  actionType?: ActionTypeFilterValue;
}

export function useOptimizationTableParams({
  activeDateFilter,
  apiFilters,
  dataLimit,
  pageOffset,
  activeRefreshState,
  accountsScope,
  actionType,
}: UseOptimizationTableParamsArgs) {
  const [searchParams] = useSearchParams();
  const [currentTime, setCurrentTime] = useState(new Date());

  const sortField = useMemo(
    () => parseSortField(searchParams.get(OPTIMIZATION_TABLE_SORT_KEY)),
    [searchParams]
  );

  const currentReportFilters = useMemo(
    () => JSON.parse(apiFilters || '[]') as SelectOption[],
    [apiFilters]
  );

  const { filtersWithoutAccount, accountIds: accountIdsFromBaseFilters } = useMemo(
    () => splitAccountFilters(currentReportFilters),
    [currentReportFilters]
  );

  // Set up time-based polling for relative date filters
  useEffect(() => {
    if (!activeRefreshState || activeRefreshState === 'Off') {
      return;
    }

    const isRelativeFilter = !activeDateFilter?.includes(CUSTOM_DATES_TITLE);
    if (!isRelativeFilter) {
      return;
    }

    const refreshItem = refreshStateMenuItems.find(item => item.title === activeRefreshState);
    const interval = refreshItem?.time || 0;

    if (interval > 0) {
      const intervalId = setInterval(() => {
        setCurrentTime(new Date());
      }, interval);

      return () => clearInterval(intervalId);
    }
  }, [activeRefreshState, activeDateFilter]);

  const [tableStartDate, tableEndDate] = useMemo(
    () => getTableDateRange(activeDateFilter),
    [activeDateFilter, currentTime]
  );

  const processedFilterSplit = useMemo(() => {
    const processedFilters = searchParams.get('processed_filters');
    if (processedFilters) {
      return splitProcessedFilterParams(JSON.parse(processedFilters) as string[]);
    }

    return {
      filtersWithoutAccount: [] as string[],
      accountIds: [] as string[],
    };
  }, [searchParams]);

  const parsedFilters = useMemo(() => {
    if (processedFilterSplit.filtersWithoutAccount.length > 0) {
      return processedFilterSplit.filtersWithoutAccount;
    }

    const result = getApiFilterFromSelectOption(filtersWithoutAccount);
    return Array.isArray(result) ? result : [result];
  }, [processedFilterSplit.filtersWithoutAccount, filtersWithoutAccount]);

  const resolvedAccountsScope = useMemo(() => {
    if (processedFilterSplit.accountIds.length > 0) {
      return processedFilterSplit.accountIds.join(',');
    }

    if (accountIdsFromBaseFilters.length > 0) {
      return accountIdsFromBaseFilters.join(',');
    }

    return accountsScope;
  }, [processedFilterSplit.accountIds, accountIdsFromBaseFilters, accountsScope]);

  const optimizationTableBaseParamsWithoutType: [string, string][] = useMemo(
    () => {
      const params: [string, string][] = [
        ['start_date', tableStartDate],
        ['end_date', tableEndDate],
        ...parsedFilters.map((f) => ['filters', f] as [string, string]),
      ];
      if (resolvedAccountsScope) {
        params.push(['accounts', resolvedAccountsScope]);
      }
      return params;
    },
    [tableStartDate, tableEndDate, parsedFilters, resolvedAccountsScope]
  );

  const optimizationTableBaseParams: [string, string][] = useMemo(
    () => {
      if (!actionType) return optimizationTableBaseParamsWithoutType;
      return [
        ...optimizationTableBaseParamsWithoutType,
        ['type', actionTypeFilterToApiType(actionType)],
      ];
    },
    [optimizationTableBaseParamsWithoutType, actionType]
  );

  const optimizationTableQueryString = useMemo(
    () =>
      new URLSearchParams([
        ...optimizationTableBaseParams,
        [
          'sortBy',
          JSON.stringify({
            field: sortField.field,
            ascending: sortField.dir === 'asc',
          }),
        ],
        ['limit', dataLimit.toString()],
        ['offset', pageOffset],
      ]).toString(),
    [optimizationTableBaseParams, sortField, pageOffset, dataLimit]
  );

  const optimizationTableCountQueryString = useMemo(
    () => new URLSearchParams(optimizationTableBaseParams).toString(),
    [optimizationTableBaseParams]
  );

  const optimizationTableCountBaseQueryString = useMemo(
    () => new URLSearchParams(optimizationTableBaseParamsWithoutType).toString(),
    [optimizationTableBaseParamsWithoutType]
  );

  const filtersSignature = useMemo(
    () => (parsedFilters.length ? parsedFilters.slice().sort().join('|') : 'none'),
    [parsedFilters]
  );

  const timeSignature = useMemo(
    () => `${tableStartDate}|${tableEndDate}`,
    [tableStartDate, tableEndDate]
  );

  return {
    sortField,
    currentReportFilters,
    parsedFilters,
    optimizationTableQueryString,
    optimizationTableCountQueryString,
    optimizationTableCountBaseQueryString,
    filtersSignature,
    timeSignature,
    resolvedAccountsScope,
  };
}
